export const formEventsConfig = {
    parentSelector: '[data-form-root="true"]',
    events: {
        // Form field focus events
        formFieldFocus: {
            selector: 'input, textarea, select',
            event: {
                event: 'form',
                component: 'form',
                action: 'focus',
                trigger: 'click',
                element: 'text_field',
                getDynamicData: (element) => {
                    let fieldName = element.getAttribute('aria-label');
                    
                    if (!fieldName) {
                        const labelId = element.getAttribute('aria-labelledby');
                        const label = labelId ? document.getElementById(labelId) : null;
                        fieldName = label ? label.textContent.trim() : null;
                    }
                    
                    if (!fieldName) {
                        fieldName = element.name || element.id || element.placeholder || '';
                    }

                    // Determine component based on URL path
                    const path = window.location.pathname;
                    const component = path.includes('/contact') ? 'contact' : 
                                   path.includes('/request-sample') ? 'request_sample' : 
                                   'form';
                    
                    return {
                        field: fieldName,
                        component: component
                    };
                }
            }
        },

        // Form field input events
        formFieldInput: {
            selector: 'input, textarea, select',
            event: {
                event: 'form',
                component: 'form',
                element: 'text_field',
                action: 'input',
                trigger: 'input',
                getDynamicData: (element) => {
                    let fieldName = element.getAttribute('aria-label');
                    
                    if (!fieldName) {
                        const labelId = element.getAttribute('aria-labelledby');
                        const label = labelId ? document.getElementById(labelId) : null;
                        fieldName = label ? label.textContent.trim() : null;
                    }
                    
                    if (!fieldName) {
                        fieldName = element.name || element.id || element.placeholder || '';
                    }

                    // Determine component based on URL path
                    const path = window.location.pathname;
                    const component = path.includes('/contact') ? 'contact' : 
                                   path.includes('/request-sample') ? 'request_sample' : 
                                   'form';
                    
                    return {
                        field: fieldName,
                        component: component
                    };
                }
            }
        },
         // Form validation result event (separate from button click)
         formValid: {
            selector: 'button[type="submit"]',
            event: {
                event: 'form',
                element: 'form',
                action: 'validation',
                trigger: 'blur',
                getDynamicData: (element) => {
                    // Find the form that contains this button
                    const form = element.closest('form');
                    if (!form) return null;

                    // Determine component based on URL path
                    const path = window.location.pathname;
                    const component = path.includes('/contact') ? 'contact' : 
                                   path.includes('/request-sample') ? 'request_sample' : 
                                   'form';

                    // Create a promise that resolves when form validation completes
                    return new Promise((resolve) => {
                        // Set a timeout to prevent infinite waiting
                        const timeout = setTimeout(() => {
                            observer.disconnect();
                            // If no errors appear within timeout, consider it a success
                            resolve({
                                component: component,
                                action_value: 'valid'
                            });
                        }, 500);

                        // Create observer to watch for error messages
                        const observer = new MutationObserver((mutations) => {
                            const errorElements = form.querySelectorAll('output[role="alert"]');
                            if (errorElements.length === 0) {
                                clearTimeout(timeout);
                                observer.disconnect();
                                resolve({
                                    component: component,
                                    action_value: 'valid'
                                });
                            }
                        });

                        // Start observing the form for changes
                        observer.observe(form, {
                            childList: true,
                            subtree: true,
                            characterData: true
                        });
                    });
                }
            }
        },

        // Form invalid event (separate from valid event)
        formInvalid: {
            selector: 'button[type="submit"]',
            event: {
                event: 'form',
                element: 'form',
                action: 'validation',
                trigger: 'focusin',
                getDynamicData: (element) => {
                    // Find the form that contains this button
                    const form = element.closest('form');
                    if (!form) return null;

                    // Determine component based on URL path
                    const path = window.location.pathname;
                    const component = path.includes('/contact') ? 'contact' : 
                                   path.includes('/request-sample') ? 'request_sample' : 
                                   'form';

                    // Create a promise that resolves when form validation completes
                    return new Promise((resolve) => {
                        // Set a timeout to prevent infinite waiting
                        const timeout = setTimeout(() => {
                            observer.disconnect();
                            // If no errors appear within timeout, don't resolve (invalid event shouldn't fire)
                            resolve(null);
                        }, 500);

                        // Create observer to watch for error messages
                        const observer = new MutationObserver((mutations) => {
                            const errorElements = form.querySelectorAll('output[role="alert"]');
                            if (errorElements.length > 0) {
                                clearTimeout(timeout);
                                observer.disconnect();

                                // Collect all errors
                                const errors = Array.from(errorElements).map(errorElement => {
                                    // Try to find the associated input field
                                    const inputField = errorElement.closest('.field')?.querySelector('input, textarea, select') || 
                                                    form.querySelector('input, textarea, select');
                                    
                                    if (!inputField) return null;

                                    // Get the field name
                                    let fieldName = inputField.getAttribute('aria-label');
                                    if (!fieldName) {
                                        const labelId = inputField.getAttribute('aria-labelledby');
                                        const label = labelId ? document.getElementById(labelId) : null;
                                        fieldName = label ? label.textContent.trim() : null;
                                    }
                                    if (!fieldName) {
                                        fieldName = inputField.name || inputField.id || inputField.placeholder || '';
                                    }

                                    return {
                                        field: fieldName,
                                        error_message: errorElement.textContent.trim()
                                    };
                                }).filter(error => error !== null);

                                if (errors.length > 0) {
                                    resolve({
                                        component: component,
                                        field: errors.map(error => error.field).join(', '),
                                        action_value: 'invalid',
                                        error_message: errors.map(error => `${error.field}: ${error.error_message}`).join('; ')
                                    });
                                } else {
                                    resolve(null);
                                }
                            }
                        });

                        // Start observing the form for changes
                        observer.observe(form, {
                            childList: true,
                            subtree: true,
                            characterData: true
                        });
                    });
                }
            }
        },

        // Form submit events (handles both error and success cases)
        formSubmit: {
            selector: 'button[type="submit"]',
            event: {
                event: 'form',
                element: 'cta_button',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                    // Find the form that contains this button
                    const form = element.closest('form');
                    if (!form) return null;

                    // Determine component based on URL path
                    const path = window.location.pathname;
                    const component = path.includes('/contact') ? 'contact' : 
                                   path.includes('/request-sample') ? 'request_sample' : 
                                   'form';

                    // Return immediate button click data
                    return {
                        component: component,
                        action_value: element.textContent.trim()
                    };
                }
            }
        }
    }
}; 